import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import i18n from '@client/i18n/config';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type AppText, AppTextForm } from './AppTextForm';

export const Route = createFileRoute('/admin-console/platform-content/comms-and-content/app-text/edit/$id')({
    component: RouteComponent,
    staticData: {
        presentation: 'overlay',
    },
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <EditAppText />
        </PageController>
    );
}

function EditAppText() {
    const { id } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const { isReadOnly } = useAdminModulePermissions('comms');

    const { data: appText, isLoading } = trpc.admin.appText.getAppText.useQuery(
        {
            id: BigInt(id),
        },
        { refetchOnWindowFocus: false },
    );

    const { mutate: updateAppText, isPending: isUpdating } = trpc.admin.appText.updateAppText.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const handleSubmit = (values: AppText) => {
        if (!appText) return;

        const key = 'update_text';
        message.loading({ content: 'Updating…', key });

        updateAppText(
            {
                id: appText.id,
                key: values.key ?? '',
                value: values.value ?? '',
                locale: values.locale ?? 'en-GB',
                namespace: values.namespace ?? 'translation',
                description: values.description,
                format: values.format ?? 'PLAIN_TEXT',
            },
            {
                onSuccess: () => {
                    message.success({ content: 'Updated', key });

                    trpcUtils.admin.appText.listAppTexts.invalidate();

                    const namespace = values.namespace ?? 'translation';
                    void i18n.reloadResources(undefined, namespace);

                    setOpen(false);
                },
                onError: (error) => {
                    message.error(error.message || 'Failed to update app text');
                },
            },
        );
    };

    return (
        <AppTextForm
            open={open}
            mode="edit"
            data={appText}
            state={match({ isLoading, isUpdating })
                .with({ isUpdating: true }, () => 'submitting' as const)
                .with({ isLoading: true }, () => 'loading' as const)
                .otherwise(() => 'ready' as const)}
            onSubmit={handleSubmit}
            onCloseFinished={() => {
                navigate({ to: '/admin-console/platform-content/comms-and-content/app-text' });
            }}
            isReadOnly={isReadOnly}
        />
    );
}
